export enum RouteGroup {
    Home = 'Home',
    About = 'About',
    Product = 'Product',
    Solutions = 'Solutions',
    Partners = 'Partners',
    Careers = 'Careers',
    BlogPost = 'BlogPost',
    Event = 'Event',
    PRArticle = 'PRArticle',
    RCArticle = 'RCArticle',
    DefaultWebPage = 'DefaultWebPage',
}

export enum Schema {
    WebSite = 'WebSite',
    ContactPoint = 'ContactPoint',
    WebPage = 'WebPage',
    VideoObject = 'VideoObject',
    Offer = 'Offer',
    Organization = 'Organization',
    FAQPage = 'FAQPage',
    Product = 'Product',
    BreadcrumbList = 'BreadcrumbList',
    PostalAddress = 'PostalAddress',
    Article = 'Article',
    Event = 'Event',
    HowTo = 'HowTo',
    JobPosting = 'JobPosting',
    AggregateRating = 'AggregateRating',
}

export interface IdFieldSchema {
    '@id': string
}

export interface ImageObjectSchema {
    '@type': Schema,
    url: string,
    width?: number | string,
    height?: number | string,
    creator?: string,
    copyrightHolder?: string,
    name?: string,
    '@id'?: string,
}

export interface PublisherSchema {
    '@id': string
    name?: string
    url?: string
}

export interface SchemaConfig {
    '@type': Schema
    '@context'?: { '@vocab': string, kg: string } | string
}

export type PeopleAudienceSchema = SchemaConfig & {
    '@id': string
    sameAs: string | string[]
    name?: string
}

export type QuantitativeValueSchema = SchemaConfig & {
    minValue: number
    maxValue: number
    unitText: string
}

export type RatingSchema = SchemaConfig & {
    bestRating: number
    ratingValue: number
    worstRating: number
}

export type AnswerSchema = SchemaConfig & {
    '@id': string
    text: string
}

export type ListItemSchema = SchemaConfig & {
    position: number
    item: string,
    name: string
}

export type QuestionSchema = SchemaConfig & {
    '@id': string
    acceptedAnswer: AnswerSchema[]
    name: string
}

export type ReviewSchema = SchemaConfig & {
    author: {
        '@type': 'Person'
        name: string
    }
    datePublished: string
    reviewBody: string
    name: string
    reviewRating: RatingSchema
}

export type AggregateRatingSchema = SchemaConfig & {
    ratingValue: string
    reviewCount: string
}

export type SearchActionSchema = SchemaConfig & {
    target: string
    name: string
    query: string
    '@id': string
}

export type PropertyValueSchema = SchemaConfig & {
    name: string
    value: string
}

export type MonetaryAmountSchema = SchemaConfig & {
    currency: string
    value: QuantitativeValueSchema
}

export type BreadcrumbListSchema = SchemaConfig & {
    itemListElement: ListItemSchema[]
}

export type ContactPointSchema = SchemaConfig & {
    availableLanguage: string
    contactType: string
    name: string
    telephone: string
    areaServed: string
    '@id': string
}

export type FAQPageSchema = SchemaConfig & {
    '@id': string
    url?: string
    author?: PublisherSchema
    audience?: PeopleAudienceSchema[]
    publisher?: PublisherSchema
    mainEntity?: QuestionSchema[]
}

export type PostalAddressSchema = SchemaConfig & {
    addressLocality: string
    streetAddress: string
    postalCode: string,
    addressCountry: string,
    addressRegion?: string
    areaServed?: string,
    name?: string
    faxNumber?: string
    '@id'?: string
}

export type OrganizationSchema = SchemaConfig & {
    name: string
    sameAs: string | string[]
    numberOfEmployees?: number
    award?: string[]
    founder?: string[]
    foundingDate?: string
    legalName?: string
    foundingLocation?: string
    logo?: string | ImageObjectSchema
    description?: string
    url?: string
    address?: PostalAddressSchema
    '@id'?: string
}

export type PlaceSchema = SchemaConfig & {
    address: { name?: string, url?: string } | PostalAddressSchema
    name?: string
}

export type VirtualLocationSchema = SchemaConfig & {
    url: string
}

export type JobPostingSchema = SchemaConfig & {
    title: string
    description: string
    identifier: PropertyValueSchema
    hiringOrganization: OrganizationSchema
    industry: string
    employmentType: string
    datePosted: string
    validThrough: string
    jobLocation: PlaceSchema,
    baseSalary: MonetaryAmountSchema
    educationRequirements: string
    experienceRequirements: string
}

export type VideoObjectSchema = SchemaConfig & {
    '@id': string
    name: string
    description: string
    contentUrl: string
    thumbnailUrl: string[]
    uploadDate: string
    embedUrl: string
    duration?: string
    publisher?: string | OrganizationSchema
    potentialAction?: {
        '@type': string
        target: string
        name?: string
        'startOffset-input'?: string
    }
}

export type PersonSchema = SchemaConfig & {
    name: string
}

export type WebPageSchema = SchemaConfig & {
    name: string,
    url: string
    publisher: OrganizationSchema | PersonSchema | string
    headline: string
    description: string
    '@id'?: string
    video?: VideoObjectSchema
}

export type OfferSchema = SchemaConfig & {
    price: number | string
    priceCurrency: string
    '@id'?: string
    name?: string
    url?: string
    availability?: string
    validFrom?: string
}

export type ProductSchema = SchemaConfig & {
    name: string
    description: string
    image: string
    aggregateRating: AggregateRatingSchema
    offers: OfferSchema,
    review: ReviewSchema[]
}

export type WebSiteSchema = SchemaConfig & {
    description: string
    isAccessibleForFree: boolean
    isFamilyFriendly: boolean
    inLanguage: string
    url: string
    name: string
    creator: OrganizationSchema
    publisher: PublisherSchema
    potentialAction: SearchActionSchema
    '@id': string
}

export type ArticleSchema = SchemaConfig & {
    '@id': string
    name: string
    url: string
    publisher: PublisherSchema
    author: PublisherSchema
    inLanguage: string
    audience: PeopleAudienceSchema | PeopleAudienceSchema[]
    image: ImageObjectSchema | ImageObjectSchema[]
    headline: string
    articleBody: string
    datePublished: string
    mainEntityOfPage: string
    hasPart?: FAQPageSchema
    video?: VideoObjectSchema
};

export type EventSchema = SchemaConfig & {
    name: string
    startDate: string
    endDate: string
    eventStatus: string
    eventAttendanceMode: string
    location: PlaceSchema
    image: string | string[]
    description: string
    offers: OfferSchema | OfferSchema[]
    performer: OrganizationSchema[] | PersonSchema[],
    organizer: OrganizationSchema | PersonSchema,
};

export interface HowToDirectionSchema {
    '@type': Schema
    text: string
}

export interface HowToStepSchema {
    '@type': Schema
    url: string
    name: string
    text: string
    image?: string | ImageObjectSchema
}

export type HowToSchema = SchemaConfig & {
    name: string,
    image: string | ImageObjectSchema,
    step: HowToStepSchema[],
}
